/**
 * Agent 平台首页 — 概览仪表盘
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAgents, AgentDTO } from '../../api/agent';

export default function AgentHomePage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgents({ page: 0, size: 6 })
      .then((res) => setAgents(res.items || []))
      .catch(() => setAgents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>SimonCat Agent</h1>
        <p style={styles.heroSubtitle}>让 AI 为您工作 — 构建、部署、管理您的智能助手</p>
        <div style={styles.heroActions}>
          <button style={styles.primaryBtn} onClick={() => navigate('/simon-cat/agents/new')}>
            + 创建 Agent
          </button>
          <button style={styles.secondaryBtn} onClick={() => navigate('/simon-cat/marketplace')}>
            浏览市场
          </button>
        </div>
      </div>

      {/* Quick Access Grid */}
      <div style={styles.quickGrid}>
        <div style={styles.quickCard} onClick={() => navigate('/simon-cat/agents')}>
          <span style={styles.quickEmoji}>🤖</span>
          <span style={styles.quickLabel}>AI Agent</span>
          <span style={styles.quickDesc}>管理您的智能助手</span>
        </div>
        <div style={styles.quickCard} onClick={() => navigate('/simon-cat/marketplace')}>
          <span style={styles.quickEmoji}>🏪</span>
          <span style={styles.quickLabel}>Agent 市场</span>
          <span style={styles.quickDesc}>发现社区精选</span>
        </div>
        <div style={styles.quickCard} onClick={() => navigate('/simon-cat/execution')}>
          <span style={styles.quickEmoji}>⚡</span>
          <span style={styles.quickLabel}>执行中心</span>
          <span style={styles.quickDesc}>查看运行历史</span>
        </div>
        <div style={styles.quickCard} onClick={() => navigate('/simon-cat/knowledge')}>
          <span style={styles.quickEmoji}>📚</span>
          <span style={styles.quickLabel}>知识库</span>
          <span style={styles.quickDesc}>管理领域文档</span>
        </div>
        <div style={styles.quickCard} onClick={() => navigate('/simon-cat/models')}>
          <span style={styles.quickEmoji}>🧠</span>
          <span style={styles.quickLabel}>模型中心</span>
          <span style={styles.quickDesc}>AI 能力状态</span>
        </div>
        <div style={styles.quickCard} onClick={() => navigate('/simon-cat/workflows')}>
          <span style={styles.quickEmoji}>⚙️</span>
          <span style={styles.quickLabel}>工作流</span>
          <span style={styles.quickDesc}>编排多步流程</span>
        </div>
      </div>

      {/* Recent Agents */}
      <div>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>最近使用</h2>
          <button style={styles.viewAllBtn} onClick={() => navigate('/simon-cat/agents')}>
            查看全部 →
          </button>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 12 }}>加载中...</div>
        ) : agents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 12 }}>
            暂无 Agent，点击上方按钮开始创建
          </div>
        ) : (
          <div style={styles.agentGrid}>
            {agents.slice(0, 6).map((agent) => (
              <div
                key={agent.agentId}
                style={styles.agentCard}
                onClick={() => navigate(`/simon-cat/agents/${agent.agentId}`)}
              >
                <span style={styles.agentAvatar}>{agent.avatar || '🤖'}</span>
                <div style={styles.agentInfo}>
                  <span style={styles.agentName}>{agent.name}</span>
                  <span style={styles.agentDesc}>{agent.description || '暂无描述'}</span>
                </div>
                <span style={styles.agentArrow}>→</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28 },
  hero: {
    textAlign: 'center' as const, padding: '40px 20px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.05) 100%)',
    borderRadius: 16, border: '1px solid rgba(99,102,241,0.12)',
  },
  heroTitle: { fontSize: 32, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #a5b4fc, #c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSubtitle: { fontSize: 14, color: '#94a3b8', margin: '8px 0 20px' },
  heroActions: { display: 'flex', gap: 10, justifyContent: 'center' },
  primaryBtn: {
    padding: '10px 24px', borderRadius: 8, border: 'none',
    background: '#6366f1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '10px 24px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
    background: 'transparent', color: '#e2e8f0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  quickGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 },
  quickCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '16px 8px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.025)', cursor: 'pointer', transition: 'all 0.2s',
  },
  quickEmoji: { fontSize: 22 },
  quickLabel: { fontSize: 11, fontWeight: 600 },
  quickDesc: { fontSize: 9, color: '#64748b', textAlign: 'center' as const },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle: { fontSize: 15, fontWeight: 600, margin: 0 },
  viewAllBtn: { background: 'none', border: 'none', color: '#a5b4fc', fontSize: 12, cursor: 'pointer' },
  agentGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  agentCard: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', transition: 'background 0.15s',
  },
  agentAvatar: { fontSize: 18 },
  agentInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 1 },
  agentName: { fontSize: 13, fontWeight: 600 },
  agentDesc: { fontSize: 11, color: '#94a3b8' },
  agentArrow: { fontSize: 12, color: '#64748b' },
};
